/**
 * Debounces {@link EventsName.MANUAL_EV_FIELD_EDITED} so keystrokes in edit boxes
 * emit one analytics event after the user pauses typing.
 */
import { useCallback, useEffect, useRef } from "react";
import type { ManualEvFieldEditedDetail } from "../helpers/evCoverageAnalyticsEvents";

const DEFAULT_DEBOUNCE_MS = 1000;

/**
 * Returns a debounced notifier for manual EV/BV field edits.
 *
 * @param onFieldEdited - Parent handler (typically enqueues BigQuery worker payload)
 * @param debounceMs - Idle delay before firing; matches form-field edit analytics
 * @returns Debounced callback; no-op when `onFieldEdited` is omitted
 */
export function useDebouncedManualEvFieldEdited(
  onFieldEdited: ((detail: ManualEvFieldEditedDetail) => void) | undefined,
  debounceMs: number = DEFAULT_DEBOUNCE_MS,
): (detail: ManualEvFieldEditedDetail) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestDetailRef = useRef<ManualEvFieldEditedDetail | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return useCallback(
    (detail: ManualEvFieldEditedDetail) => {
      if (!onFieldEdited) {
        return;
      }

      latestDetailRef.current = detail;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        const pending = latestDetailRef.current;
        if (pending) {
          onFieldEdited(pending);
        }
        timeoutRef.current = null;
      }, debounceMs);
    },
    [debounceMs, onFieldEdited],
  );
}
